// Pure time helpers for the HR "Edit Attendance" page.
//
// TIME CONVENTION ("fake UTC") — read before touching anything here:
// check-in / check-out Dates in the checkIns collection hold PAKISTAN wall-clock
// digits written as if they were UTC (6:05 PM PKT on 24 Sep is stored as
// 2026-09-24T18:05:00.000Z). So:
//   - a wall-clock string "YYYY-MM-DDTHH:mm" maps to Date.UTC(...) of those digits,
//   - a stored ISO maps back to the string with toISOString().slice(0, 16),
//   - display always uses UTC getters / timeZone: "UTC".
// NO timezone offset is ever applied to check-in/check-out values, and nothing here
// may call new Date("YYYY-MM-DDTHH:mm") without a "Z" (that would be browser-local).
// The only +5h is for turning the REAL current time (Date.now()) or a REAL audit
// timestamp (lastEdit.at) into Pakistan wall-clock terms.

export const PKT_OFFSET_MS = 5 * 60 * 60 * 1000;
export const MINUTE_MS = 60 * 1000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

// Same rule as a normal checkout (HR-backend routes/timerData.js).
export const OVERTIME_THRESHOLD_HOURS = 9 + 25 / 60; // 9h 25m
export const MAX_SHIFT_MS = 24 * HOUR_MS;
export const FUTURE_GRACE_MS = 10 * MINUTE_MS;
// An employee can still check out on their own until check-in + 12h.
export const SELF_CHECKOUT_WINDOW_MS = 12 * HOUR_MS;
export const MAX_RANGE_DAYS = 62;
export const MAX_NOTE = 500;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** Current Pakistan wall clock, in fake-UTC milliseconds. */
export const pktNowWallMs = () => Date.now() + PKT_OFFSET_MS;

/** Today's date in Pakistan, "YYYY-MM-DD" (independent of the browser's timezone). */
export const pktToday = () => new Date(pktNowWallMs()).toISOString().slice(0, 10);

/**
 * Yesterday's date in Pakistan. The default filter starts here: shifts run ~6 PM to ~3 AM
 * and are filed under their check-in date, so last night's shift — the one whose missed
 * check-outs HR fixes the next morning — is dated yesterday.
 */
export const pktYesterday = () =>
  new Date(pktNowWallMs() - DAY_MS).toISOString().slice(0, 10);

/** "YYYY-MM-DD" -> ms at 00:00 of that day, or null if not a real calendar date. */
export function parseDateOnly(s) {
  const m = DATE_RE.exec(typeof s === "string" ? s : "");
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const ms = Date.UTC(y, mo - 1, d);
  const dt = new Date(ms);
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d
  ) {
    return null;
  }
  return ms;
}

/** "YYYY-MM-DDTHH:mm" (PKT wall clock) -> fake-UTC ms, or null if invalid (e.g. Feb 30, 24:00). */
export function parseWall(s) {
  const m = WALL_RE.exec(typeof s === "string" ? s : "");
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const hh = Number(m[4]);
  const mi = Number(m[5]);
  const ms = Date.UTC(y, mo - 1, d, hh, mi);
  const dt = new Date(ms);
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d ||
    dt.getUTCHours() !== hh ||
    dt.getUTCMinutes() !== mi
  ) {
    return null;
  }
  return ms;
}

/** fake-UTC ms -> "YYYY-MM-DDTHH:mm". */
export const wallFromMs = (ms) => new Date(ms).toISOString().slice(0, 16);

/** Stored ISO -> "YYYY-MM-DDTHH:mm" for a datetime-local input ("" when missing/invalid). */
export function isoToWall(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : wallFromMs(t);
}

/** Datetime-local inputs may report seconds ("…T18:05:30"); the API takes minutes only. */
export const normalizeWall = (v) =>
  typeof v === "string" && v.length > 16 ? v.slice(0, 16) : v || "";

const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "UTC",
});
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const DATETIME_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "UTC",
});

export const fmtTimeMs = (ms) => TIME_FMT.format(new Date(ms));
export const fmtDateMs = (ms) => DATE_FMT.format(new Date(ms));

/** Stored ISO -> "6:05 PM" (wall clock read with UTC getters). */
export function fmtTime(iso) {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "—" : fmtTimeMs(t);
}

/** Stored ISO -> "Thu, Sep 24, 2026". */
export function fmtDate(iso) {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "—" : fmtDateMs(t);
}

/** A REAL instant (e.g. lastEdit.at) -> "Sep 24, 2026, 3:47 AM PKT". */
export function fmtRealPkt(iso) {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "" : `${DATETIME_FMT.format(new Date(t + PKT_OFFSET_MS))} PKT`;
}

/** 33120000 -> "9h 12m"; null/negative -> "—". */
export function fmtDuration(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const totalMin = Math.floor(ms / MINUTE_MS);
  return `${Math.floor(totalMin / 60)}h ${totalMin % 60}m`;
}

/** Whole calendar days between the wall-clock dates of two fake-UTC instants. */
export function dayDiff(fromMs, toMs) {
  const a = Math.floor(fromMs / DAY_MS);
  const b = Math.floor(toMs / DAY_MS);
  return b - a;
}

/** Status a checkout of this length gets — mirrors the normal checkout exactly. */
export function statusForDuration(durationMs) {
  const hours = durationMs / HOUR_MS;
  return hours < OVERTIME_THRESHOLD_HOURS ? "accepted" : "pending";
}

/** Filter guard: "" when OK, otherwise a message. */
export function validateRange(from, to) {
  const a = parseDateOnly(from);
  const b = parseDateOnly(to);
  if (a == null || b == null) return "Pick both a From and a To date.";
  if (b < a) return "The To date can't be before the From date.";
  const days = Math.round((b - a) / DAY_MS) + 1;
  if (days > MAX_RANGE_DAYS) {
    return `Pick a range of ${MAX_RANGE_DAYS} days or less (this one covers ${days} days).`;
  }
  return "";
}

/**
 * Validate a pair of wall-clock strings the same way the backend does.
 * checkOutRequired: true for new entries and for records that already have a check-out.
 * inExactMs / outExactMs: the STORED value (with its seconds) when that field was left
 * unchanged — the server keeps the stored value in that case, so the duration and status
 * preview must use it too, or a shift ending in the 9h25m boundary minute would preview
 * "Pending" and save "Accepted".
 */
export function evaluateEntry({
  checkIn,
  checkOut,
  checkOutRequired,
  requiredCheckOutMessage,
  nowWallMs = pktNowWallMs(),
  inExactMs = null,
  outExactMs = null,
}) {
  const errors = {};
  const latest = nowWallMs + FUTURE_GRACE_MS;

  let inMs = null;
  if (!checkIn) {
    errors.checkIn = "Enter the check-in date and time.";
  } else {
    inMs = inExactMs != null ? inExactMs : parseWall(checkIn);
    if (inMs == null) errors.checkIn = "Enter a valid date and time.";
    else if (inMs > latest) errors.checkIn = "Check-in can't be in the future.";
  }

  let outMs = null;
  if (!checkOut) {
    if (checkOutRequired) {
      errors.checkOut = requiredCheckOutMessage || "Enter the check-out date and time.";
    }
  } else {
    outMs = outExactMs != null ? outExactMs : parseWall(checkOut);
    if (outMs == null) {
      errors.checkOut = "Enter a valid date and time.";
    } else if (inMs != null && outMs <= inMs) {
      errors.checkOut =
        "Check-out must be after check-in. If the shift ended after midnight, pick the next day's date.";
    } else if (inMs != null && outMs - inMs > MAX_SHIFT_MS) {
      errors.checkOut = "A shift can't be longer than 24 hours.";
    } else if (outMs > latest) {
      errors.checkOut = "Check-out can't be in the future.";
    }
  }

  const valid = !errors.checkIn && !errors.checkOut;
  const durationMs = valid && inMs != null && outMs != null ? outMs - inMs : null;
  return {
    errors,
    valid,
    inMs,
    outMs,
    durationMs,
    status: durationMs != null ? statusForDuration(durationMs) : null,
    nextDay: durationMs != null ? dayDiff(inMs, outMs) : 0,
  };
}
